import { container } from "tsyringe";
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import store from "@/store";
import { StiSCPresenter } from "@/app/ui/presenters/StiSCPresenter";
import {
  StiSCEntities,
  SttPiecesStiScDetail,
  StiScManifestData,
  RequestListSTISCSession,
  PayloadProcessUpdateSTISC,
  RequestListSTISC,
  PayloadFinishSessionSTISC,
  STISCReverseScanTemporary,
  STISCReverseScanCreate,
  STISCSTTDueSummary,
  STISCListBookedEntities,
  STISCScanFailed,
  STIScSTTDueEntities,
  STIScListBookedEntities
} from "@/domain/entities/StiSc";
import { Pagination } from "@/domain/entities/Pagination";
import {
  CreateSTISCReverseScanRequest,
  FinishSessionSTISCRequest,
  GenerateApiRequest,
  STISCReverseScanManifestRequest,
  STISCReverseScanTemporaryListRequest,
  STIScSTTDueListRequest,
  STISCSTTDueSummaryRequest,
  UpdateToSTISCRequest
} from "@/data/payload/api/StiScApiRequest";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import { MainAppController } from "./MainAppController";
import {
  FAILED_ERROR,
  parsingErrorResponse,
  storeDatatoLocalStorage
} from "@/app/infrastructures/misc/Utils";
import { playNotification } from "@/app/infrastructures/misc/UtilsAudio";
import { ErrorMessageEntities, OptionsClass } from "@/domain/entities/MainApp";

export interface StiSCState {
  isLoading: boolean;
  errorModal: boolean;
  errorCause: string;
}

@Module({ namespaced: true, dynamic: true, store, name: "sti_sc" })
class StiSCStore extends VuexModule implements StiSCState {
  public isLoading = false;
  public periodeStart: any = "";
  public periodeEnd: any = "";
  public stiSCList = new StiSCEntities(new Pagination(0, 0), []);
  public isError = false;
  public errorModal = false;
  public errorCause = "";
  public isOpenSuccess = false;
  public isLoadingPrint = false;
  public isErrorPrint = false;
  public errorPrintCause = "";
  public stiScIdToGeneratePdf = 0;
  public stiSCManifestData = new StiScManifestData(
    0,
    "",
    "",
    "",
    0,
    0,
    0,
    0,
    "",
    0,
    []
  );
  public failedStt: Array<any> = [];
  public sttDueListData = new STIScSTTDueEntities();
  public bookedListData: STIScListBookedEntities = new STIScListBookedEntities();

  public isLoadingSTTSummary = false;
  public STISCSTTDueSummaryData: STISCSTTDueSummary = new STISCSTTDueSummary();
  public search = "";
  public STISCReverseScanTemporaryList: STISCReverseScanTemporary[] = [];
  public STISCReverseScanCreateData: STISCReverseScanCreate = new STISCReverseScanCreate();
  public scanFailedData: STISCScanFailed[] = [];
  public bookedListOptions: OptionsClass[] = [];

  @Action
  public getListStiSC(params: RequestListSTISC) {
    this.setIsLoading(true);
    const presenter = container.resolve(StiSCPresenter);
    return presenter
      .getListStiSC(params)
      .then((res: StiSCEntities) => {
        this.setStiSCList(res);
        this.setErrorCause("");
      })
      .catch((err: any) => {
        this.setErrorCause(err.response ? "server" : "internet");
        this.setStiSCList(new StiSCEntities(new Pagination(0, 0), []));
      })
      .finally(() => this.setIsLoading(false));
  }

  @Action
  public fetchStiSCList(params: {isStoSc: boolean}) {
    this.getListStiSC(new RequestListSTISC({
      page: this.stiSCList.pagination.page,
      limit: this.stiSCList.pagination.limit,
      startDate: this.periodeStart ? new Date(this.periodeStart).toLocaleDateString("fr-CA") : "",
      endDate: this.periodeEnd ? new Date(this.periodeEnd).toLocaleDateString("fr-CA") : "",
      isStoScPurposes: params.isStoSc,
      search: this.search
    }));
  }

  @Action
  public async getDetailSttNumber(params: {
    sttNumber: string;
    callback: any;
  }) {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();
    const presenter = container.resolve(StiSCPresenter);
    return await presenter
      .getDetailSttPieces(params.sttNumber)
      .then((res: SttPiecesStiScDetail) => {
        params.callback(res);
        return res;
      })
      .catch((err: any) => {
        playNotification("error");
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Tambah No. STT Gagal!", () =>
            this.getDetailSttNumber(params)
          )
        );
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Action
  public onGenerateStiSc(params: { sttNumber: Array<string> }) {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();
    const presenter = container.resolve(StiSCPresenter);
    return presenter
      .generateStiSc(new GenerateApiRequest(params.sttNumber))
      .then((res: ResponsePayload) => {
        this.setStiScIdToGeneratePdf(res.stiScId);
        return res;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Perubahan Status Gagal!", () =>
            this.onGenerateStiSc(params)
          )
        );
        return new ResponsePayload();
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Action
  public async validateSttPiece(sttPiece: string) {
    const presenter = container.resolve(StiSCPresenter);
    return presenter
      .validationScanSTTStiSc(sttPiece)
      .then(res => {
        return {
          isAllowUpdateStatus: res.isAllowUpdateStatus,
          errorMessage: res.errorMessage
        };
      })
      .catch((err: any) => {
        return {
          isAllowUpdateStatus: false,
          errorMessage:
            (err.response && err.response.data.message.id) ||
            "No. STT Pieces tidak ditemukan"
        };
      });
  }

  @Action
  public getStiSCManifestDetail(params: { id: number }) {
    this.setIsLoadingPrint(true);
    const presenter = container.resolve(StiSCPresenter);
    return presenter
      .getStiSCManifestDetail(params.id)
      .then((res: StiScManifestData) => {
        this.setStiSCManifestData(res);
        this.setErrorPrint(false);
        this.setErrorPrintCause("");
        return true;
      })
      .catch((err: any) => {
        this.setErrorPrint(true);
        this.setErrorPrintCause(err.response ? "server" : "internet");
        this.setStiSCManifestData(
          new StiScManifestData(0, "", "", "", 0, 0, 0, 0, "", 0, [])
        );
        return false;
      })
      .finally(() => this.setIsLoadingPrint(false));
  }

  @Action
  public getSTISCSession(param: RequestListSTISCSession) {
    const presenter = container.resolve(StiSCPresenter);
    return presenter.getStiSCSession(param);
  }

  @Action
  public createSTISCSession() {
    const presenter = container.resolve(StiSCPresenter);
    return presenter.createStiSCSession();
  }

  @Action
  public proccessUpdateSTISC(param: PayloadProcessUpdateSTISC) {
    const presenter = container.resolve(StiSCPresenter);
    return presenter.processUpdateSTISC(new UpdateToSTISCRequest({manifestId: param.manifestId, sttNo: param.sttNo}));
  }

  @Action
  public finishSessionSTISC(param: PayloadFinishSessionSTISC) {
    const presenter = container.resolve(StiSCPresenter);
    return presenter.finishStiSCSession(new FinishSessionSTISCRequest({manifestId: param.manifestId}));
  }

  @Action
  public getStiScBookedList(): Promise<STIScListBookedEntities> {
    const presenter = container.resolve(StiSCPresenter);
    this.setIsLoading(true);

    return new Promise((resolve, reject) => {
      presenter
        .getBookedList()
        .then(res => {
          this.setSTIScBookedListData(res);
          resolve(res);
        })
        .catch(err => {
          MainAppController.showErrorMessage(
            parsingErrorResponse(
              err,
              "Gagal mengambil data POS/Klien",
              this.getStiScBookedList,
              MainAppController.closeErrorMessage
            )
          );
          reject(err);
        })
        .finally(() => {
          this.setIsLoading(false);
        });
    });
  }

  @Action
  public getStiSttDueList(
    params: STIScSTTDueListRequest
  ): Promise<STIScSTTDueEntities> {
    this.setSTTDueData(new STIScSTTDueEntities());
    const presenter = container.resolve(StiSCPresenter);

    return new Promise((resolve, reject) => {
      this.setIsLoading(true);

      presenter
        .getSTTDueList(params)
        .then(res => {
          this.setErrorCause("");
          this.setSTTDueData(res);
          resolve(res);
        })
        .catch(error => {
          this.setErrorCause(error.response ? "server" : "internet");
          reject(error);
        })
        .finally(() => {
          this.setIsLoading(false);
        });
      });
    }
  @Action
  public getSTISCSTTDueSummary(): Promise<STISCSTTDueSummary> {
    this.setIsLoadingSTTSummary(true);
    const presenter = container.resolve(StiSCPresenter);

    return new Promise((resolve, reject) => {
      presenter
        .getSTISCSTTDueSummary()
        .then(res => {
          this.setSTISCSTTDueSummaryData(res);
          resolve(res);
        })
        .catch(error => {
          const message = error.response.data.message;
          MainAppController.showErrorMessage(
            parsingErrorResponse(
              typeof message === "string" ? error.response : error,
              "Gagal mengambil data summary"
            )
          );
          reject(error);
        })
        .finally(() => {
          this.setIsLoadingSTTSummary(false);
        });
    });
  }

  @Action
  public downloadSttDue(params: STIScSTTDueListRequest) {
    MainAppController.showLoading();
    const presenter = container.resolve(StiSCPresenter);
    presenter
      .downloadSttDue(params)
      .catch(err => {
        const message = err.response.data.message;
        MainAppController.showErrorMessage(
          typeof message === "string"
            ? new ErrorMessageEntities({
                message: message,
                title: "Server Error",
                type: FAILED_ERROR
              })
            : parsingErrorResponse(
                err,
                "Gagal mengunduh data STT belum di STI!"
              )
        );
      })
      .finally(() => MainAppController.closeLoading());
    }
    @Action
  public downloadSTISCExcel(id: number) {
    MainAppController.showLoading();
    const presenter = container.resolve(StiSCPresenter);
    presenter
      .downloadSTISCExcel(id)
      .catch(err =>
        MainAppController.showErrorMessage(parsingErrorResponse(err))
      )
      .finally(() => MainAppController.closeLoading());
  }

  @Action
  public getListSTISCReverseScanTemporary(
    params: STISCReverseScanTemporaryListRequest
  ): Promise<STISCReverseScanTemporary[]> {
    const presenter = container.resolve(StiSCPresenter);

    return new Promise((resolve, reject) => {
      presenter
        .getStiScReverseScanTemporary(params)
        .then(res => {
          this.setSTISCReverseScanTemporary(res);
          resolve(res);
        })
        .catch(err => {
          const message = err.response.data.message;
          MainAppController.showErrorMessage(
            typeof message === "string"
              ? new ErrorMessageEntities({
                  message: message,
                  title: "Server Error",
                  type: FAILED_ERROR
                })
              : parsingErrorResponse(err, "Error")
          );
          reject(err);
        });
    });
  }

  @Action
  public createSTISCReverseScan(
    payload: CreateSTISCReverseScanRequest
  ): Promise<STISCReverseScanCreate> {
    this.setSTISCReverseScanCreateData(new STISCReverseScanCreate());
    MainAppController.showLoading();
    const presenter = container.resolve(StiSCPresenter);

    return new Promise((resolve, reject) => {
      presenter
        .createStiScReverseScan(payload)
        .then(res => {
          this.setSTISCReverseScanCreateData(res);
          resolve(res);
        })
        .catch(err => {
          const message = err.response.data.message;

          if (typeof message !== "string") {
            let scanFailedData = this.scanFailedData;
            const errorMessage = message;

            if (
              this.scanFailedData.find(data => data.sttNo === payload.sttNo)
            ) {
              scanFailedData = scanFailedData.map(data => {
                if (data.sttNo === payload.sttNo) {
                  return {
                    ...data,
                    errorMessage
                  };
                }

                return data;
              });
            } else {
              scanFailedData.unshift({
                sttNo: payload.sttNo,
                errorMessage
              });
            }

            this.setScanFailedData(scanFailedData);
            storeDatatoLocalStorage("sti-sc-scan-failed", scanFailedData);
          }

          reject(typeof message === "string" ? err.response : err);
        })
        .finally(() => {
          MainAppController.closeLoading();
        });
    });
  }

  @Action
  public getStiScSttDueSummary(
    params: STISCSTTDueSummaryRequest
  ): Promise<STISCSTTDueSummary> {
    const presenter = container.resolve(StiSCPresenter);

    return new Promise((resolve, reject) => {
      presenter
        .getStiScSttDueSummary(params)
        .then(res => {
          this.setSTISCSTTDueSummaryData(res);
          resolve(res);
        })
        .catch(error => {
          const message = error.response.data.message;
          MainAppController.showErrorMessage(
            parsingErrorResponse(
              typeof message === "string" ? error.response : error,
              "Gagal mengambil data summary"
            )
          );
          reject(error);
        });
    });
  }

  @Action
  public stiScReverseScanManifest(payload: STISCReverseScanManifestRequest): Promise<ResponsePayload> {
    const presenter = container.resolve(StiSCPresenter);
    return new Promise((resolve, reject) => {
      presenter
        .stiScReverseScanManifest(payload)
        .then(res => resolve(res))
        .catch(err => reject(err));
    });
  }

  @Mutation
  setIsLoadingPrint(val: boolean) {
    this.isLoadingPrint = val;
  }

  @Mutation
  setStiScIdToGeneratePdf(val: number) {
    this.stiScIdToGeneratePdf = val;
  }

  @Mutation
  setStiSCManifestData(val: StiScManifestData) {
    this.stiSCManifestData = val;
  }

  @Mutation
  setErrorPrint(val: boolean) {
    this.isErrorPrint = val;
  }

  @Mutation
  setErrorPrintCause(val: string) {
    this.errorPrintCause = val;
  }

  @Mutation
  public setOpenSuccess(value: boolean) {
    this.isOpenSuccess = value;
  }

  @Mutation
  public setStiSCList(value: StiSCEntities) {
    this.stiSCList = value;
  }

  @Mutation
  public setIsLoading(value: boolean) {
    this.isLoading = value;
  }

  @Mutation
  public setPeriodeStart(value: Date | null) {
    this.periodeStart = value;
  }

  @Mutation
  public setPeriodeEnd(value: Date | null) {
    this.periodeEnd = value;
  }

  @Mutation
  public setError(value: boolean) {
    this.isError = value;
  }

  @Mutation
  public setErrorModal(value: boolean) {
    this.errorModal = value;
  }

  @Mutation
  public setErrorCause(value: string) {
    this.errorCause = value;
  }

  @Mutation
  public setSttFailed(value: Array<any>) {
    this.failedStt = value;
  }


  @Mutation
  public setSTIScBookedListData(data: STIScListBookedEntities): void {
    this.bookedListData = data;
  }

  @Mutation
  public setSTTDueData(data: STIScSTTDueEntities): void {
    this.sttDueListData = data;
  }

  @Mutation
  public setFirstPageSTTDue(): void {
    this.sttDueListData.pagination.page = 1;
  }
  @Mutation
  public setSTISCSTTDueSummaryData(data: STISCSTTDueSummary): void {
    this.STISCSTTDueSummaryData = data;
  }

  @Mutation
  public setIsLoadingSTTSummary(value: boolean) {
    this.isLoadingSTTSummary = value;
  }

  @Mutation
  public setSearch(val: string) {
    this.search = val;
  }

  @Action
  public setFirstPage() {
    this.stiSCList.pagination.page = 1;
  }

  @Mutation
  public setSTISCReverseScanTemporary(data: STISCReverseScanTemporary[]): void {
    this.STISCReverseScanTemporaryList = data.reverse();
  }

  @Mutation
  public setSTISCReverseScanCreateData(data: STISCReverseScanCreate): void {
    this.STISCReverseScanCreateData = data;
  }


  @Mutation
  public setSTISCBookedListData(data: STISCListBookedEntities): void {
    this.bookedListData = data;
  }

  @Mutation
  public setScanFailedData(data: STISCScanFailed[]): void {
    this.scanFailedData = data;
  }

  @Mutation
  public setBookedListOptions(data: OptionsClass[]): void {
    this.bookedListOptions = data;
  }
}

export const StiSCController = getModule(StiSCStore);
